import React, { useState, useRef, useEffect } from 'react'
import { TeaserData } from './teaser'
import { AmazonAffiliateBoxTeaserEntry, ImageEntry} from '../helper/interfaces';
import { getEnclosureLink, getExternalLink } from "../helper/link";
import { ChevronRight } from "react-feather";
import { useLoggedRouter } from '../helper/useLoggedRouter';
import { getImageUrlFormat1_1_500 } from '../helper/images';

type TeaserAmazonAffiliateBoxProps = TeaserData & { navigate?: (id?: string, ressortId?: string) => void };

export default function TeaserAmazonAffiliateBox(data: TeaserAmazonAffiliateBoxProps) {
  const [routerPush] = useLoggedRouter();

  const [showAffiliateInfo, setShowAffiliateInfo] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const infoIconRef = useRef<HTMLSpanElement>(null);
  const closeIconRef = useRef<HTMLSpanElement>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [tooltipWidth, setTooltipWidth] = useState(300);

  const updateTooltipPosition = () => {
    const iconRef = showAffiliateInfo ? closeIconRef : infoIconRef;
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      const padding = 10;
      const viewportWidth = window.innerWidth;
      const maxTooltipWidth = Math.min(300, viewportWidth - (padding * 2));
      setTooltipWidth(maxTooltipWidth);
      
      // Rechtsbündig zum Icon: rechter Rand des Tooltips auf Höhe des rechten Rands des Icons
      const iconRightEdge = rect.right;
      let leftPosition = iconRightEdge - maxTooltipWidth;
      
      // Überprüfen, ob die Box über den linken Rand hinausragt
      if (leftPosition < padding) {
        leftPosition = padding;
      }
      
      // Überprüfen, ob die Box über den rechten Rand hinausragt
      const rightEdge = leftPosition + maxTooltipWidth;
      if (rightEdge > viewportWidth - padding) {
        leftPosition = viewportWidth - maxTooltipWidth - padding;
      }
      
      setTooltipPosition({
        top: rect.bottom + window.scrollY + 8,
        left: leftPosition + window.scrollX
      });
    }
  };

  useEffect(() => {
    if (showAffiliateInfo) {
      setIsAnimating(true);
      updateTooltipPosition();
      window.addEventListener('scroll', updateTooltipPosition);
      window.addEventListener('resize', updateTooltipPosition);
    } else {
      window.removeEventListener('scroll', updateTooltipPosition);
      window.removeEventListener('resize', updateTooltipPosition);
    }
    return () => {
      window.removeEventListener('scroll', updateTooltipPosition);
      window.removeEventListener('resize', updateTooltipPosition);
    };
  }, [showAffiliateInfo]);

  const handleOpenInfo = () => {
    setShowAffiliateInfo(true);
  };

  const handleCloseInfo = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setShowAffiliateInfo(false);
    }, 300);
  };

  const teaser = data.teaser as AmazonAffiliateBoxTeaserEntry;

  const products = teaser.products ?? [];

  const internalNavigate = async (e, id) => {
    e.stopPropagation();
    e.preventDefault();

    if (data.navigate) {
      data.navigate(id, undefined);
    } else {
      await routerPush(`/article/${encodeURIComponent(id)}`);
    }
  };

  return <React.Fragment>
    {products.map((product, index) => {
      const link = getExternalLink(product);
      const savingsPercentage = product?.savingsPercentage ?? 0;
      
      let imageUrl = (product.images?.length > 0 ) ? getEnclosureLink(product.images[0]) : null;
      if(teaser.originalType !== "amazonaffiliatebox" && imageUrl) {
        imageUrl = getImageUrlFormat1_1_500(product.images[0] as ImageEntry);
      }

      return (
        <div className="affiliate-box" key={`teaser-amazon-affiliate-box-${index}-${teaser.id}`}>
          <div className="flex justify-between">
            <div className="tag-ad mb-2">
              <span>Anzeige</span>            
            </div>
            <div className="hint-ad mb-2">
              {!showAffiliateInfo ? (
                <div className="text-right">
                  <span 
                    ref={infoIconRef}
                    className="icon-info inline-icon cursor-pointer" 
                    onClick={handleOpenInfo}
                  ></span>
                </div>
              ) : (
                <div className="text-right">
                  <span 
                    ref={closeIconRef}
                    className="icon-cancel-circle cursor-pointer" 
                    onClick={handleCloseInfo}
                  ></span>
                </div>
              )}
            </div>
          </div>
          {showAffiliateInfo && (
            <div 
              className={`affiliate-hint-box-tooltip ${isAnimating ? 'fade-in' : 'fade-out'}`}
              style={{
                position: 'absolute',
                top: `${tooltipPosition.top}px`,
                left: `${tooltipPosition.left}px`,
                width: `${tooltipWidth}px`,
                maxWidth: '300px',
                zIndex: 30
              }}
            >
              <span className="icon-info inline-icon"></span> <strong>Affiliate-Link:</strong> Dieser Artikel enthält Affiliate-Links. Alle Produkte werden von der Redaktion unabhängig ausgewählt. 
              Im Falle eines Kaufs des Produkts nach Klick auf den Link erhalten wir ggf. eine Provision. 
              Mehr dazu lesen Sie <a className="nb-affiliate-link" onClick={(e) => {internalNavigate(e, '1.11752875')}}>hier</a>.
            </div>
          )}
          <div>
            <a key={`product-${index}-${teaser.id}`} href={link} target="_blank" rel="noreferrer" className="affiliate-box-product">
              <div className="flex gap-4">
                <div className="w-1/3 affiliate-box-product-image">
                  {imageUrl && <div className=""><img src={imageUrl} className="w-full" /></div>}
                  {savingsPercentage > 0 && <div className="affiliate-box-product-percentage">-{savingsPercentage} %</div>}
                  {product.savings && <div className="affiliate-box-product-percentage">{product.savings}</div>}
                </div>
                <div className="w-2/3">
                  {product.partner && <p className="affiliate-box-product-partner mb-4 line-clamp-3">{product.partner}</p>}
                  {product.title && <p className="mb-4 line-clamp-3">{product.title}</p>}
                  <div className="flex gap-4 items-center justify-between">
                    <div className="amazon-logo">
                      {product.originalPrice && <div className="affiliate-box-product-originalprice">{product.originalPrice}</div>}
                      <div className="affiliate-box-product-price mb-2">{product.price}</div>
                      {!product.partner && <img src="/logos/amazon-logo.svg" alt="Amazon" className="mb-2" />}
                    </div>
                    <div className="affiliate-box-product-button ">
                      <button className="btn-ad">Zum Angebot <ChevronRight /></button>
                    </div>
                  </div>
                </div>
              </div>
            </a>
          </div>
        </div>
      )
    })}
  </React.Fragment>
}